import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Loader2, Plus, Pencil, Trash2, Copy, GripVertical, Upload, X, Check,
  Eye, EyeOff, Save, Send, Smartphone, AlertTriangle, ArrowUp, ArrowDown,
} from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ALLERGENS, NUTRITION_FIELDS } from "@/i18n";
import { AllergenBadge } from "@/components/AllergenBadge";

const LANGS = [["fr", "Français"], ["nl", "Nederlands"], ["en", "English"]];
const ALLERGEN_KEYS = Object.keys(ALLERGENS);
const emptyNutrition = () => NUTRITION_FIELDS.reduce((a, f) => ({ ...a, [f.key]: "" }), {});
const emptyProduct = () => ({
  slug: "", name: { fr: "", nl: "", en: "" }, description: { fr: "", nl: "", en: "" },
  ingredients: { fr: "", nl: "", en: "" }, allergens: [], allergens_detail: { fr: "", nl: "", en: "" },
  composition: [], nutrition: emptyNutrition(), image: "", available: true, hidden: false, order: 0,
});

function Confirm({ title, message, confirmLabel, onConfirm, onCancel, danger }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-5">
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} />
      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 text-center" data-testid="confirm-dialog">
        <div className={`mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full ${danger ? "bg-red-100 text-red-500" : "bg-amber-100 text-amber-600"}`}>
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h3 className="caneat-title text-lg text-caneat-ink">{title}</h3>
        <p className="mt-1 text-sm text-caneat-muted">{message}</p>
        <div className="mt-5 flex gap-2">
          <button onClick={onCancel} className="flex-1 rounded-full bg-caneat-pageCream py-2.5 text-sm font-bold text-caneat-ink">Annuler</button>
          <button onClick={onConfirm} data-testid="confirm-ok"
            className={`flex-1 rounded-full py-2.5 text-sm font-bold text-white ${danger ? "bg-red-500" : "bg-caneat-orange"}`}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>, document.body);
}

function PhonePreview({ form, onClose }) {
  const tl = (o) => (o && (o.fr || Object.values(o)[0])) || "";
  return createPortal(
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 flex flex-col items-center" data-testid="phone-preview">
        <div className="mb-3 flex items-center gap-2 text-white"><Smartphone className="h-4 w-4" /> <span className="text-sm font-bold">Aperçu client</span></div>
        <div className="h-[640px] w-[330px] overflow-hidden rounded-[2.2rem] border-8 border-caneat-ink bg-caneat-pageCream shadow-2xl">
          <div className="no-scrollbar h-full overflow-y-auto">
            <div className="relative flex h-48 items-center justify-center caneat-doodle-bg">
              {form.image ? <img src={resolveImage(form.image)} alt="" className="h-40 w-auto object-contain drop-shadow-2xl" /> : <span className="text-white/70">Photo</span>}
            </div>
            <div className="px-5 pb-6 pt-4">
              <h2 className="caneat-title text-2xl text-caneat-ink">{tl(form.name) || "Nom du produit"}</h2>
              <p className="mt-1 text-sm text-caneat-muted">{tl(form.description)}</p>
              {form.composition?.length > 0 && (
                <div className="mt-4">
                  <h3 className="caneat-title text-base text-caneat-ink">Composition</h3>
                  {form.composition.map((c, i) => (
                    <div key={i} className="mt-1.5 flex justify-between text-xs"><span className="font-semibold text-caneat-ink">{tl(c.label)}</span><span className="font-bold text-caneat-orange">{String(c.pct).replace(".", ",")} %</span></div>
                  ))}
                </div>
              )}
              <div className="mt-4 rounded-xl border-2 border-amber-300 bg-amber-50 p-3">
                <h3 className="caneat-title text-sm uppercase text-amber-900">Allergènes</h3>
                <div className="mt-2 flex flex-wrap gap-1.5">{(form.allergens || []).map((a) => <AllergenBadge key={a} keyName={a} />)}</div>
                {tl(form.allergens_detail) && <p className="mt-2 text-[11px] font-semibold text-amber-900">{tl(form.allergens_detail)}</p>}
              </div>
              <h3 className="caneat-title mt-4 text-base text-caneat-ink">Ingrédients</h3>
              <p className="mt-1 rounded-xl bg-white p-3 text-xs text-caneat-ink ring-1 ring-caneat-cream">{tl(form.ingredients)}</p>
            </div>
          </div>
        </div>
        <button onClick={onClose} className="mt-3 rounded-full bg-white px-5 py-2 text-sm font-bold text-caneat-ink">Fermer l'aperçu</button>
      </div>
    </div>, document.body);
}

function Editor({ initial, onClose, onSaved }) {
  const { authHeaders } = useAuth();
  const [form, setForm] = useState({ ...emptyProduct(), ...initial, nutrition: { ...emptyNutrition(), ...(initial.nutrition || {}) }, composition: initial.composition || [] });
  const [tab, setTab] = useState("fr");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const fileRef = useRef();
  const isNew = !initial.id;

  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const upd = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setML = (field, lang, val) => { setForm((f) => ({ ...f, [field]: { ...f[field], [lang]: val } })); setDirty(true); };
  const toggleAllergen = (k) => upd({ allergens: form.allergens.includes(k) ? form.allergens.filter((x) => x !== k) : [...form.allergens, k] });

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } });
      upd({ image: data.url });
    } catch { setError("Échec de l'upload"); } finally { setUploading(false); }
  };

  const validate = () => {
    if (!form.slug || !form.name.fr) { setError("Le slug et le nom (FR) sont requis"); return false; }
    return true;
  };

  const saveDraft = async () => {
    if (!validate()) return;
    setBusy(true); setError("");
    try {
      if (isNew) {
        const { data } = await api.post("/admin/products", { ...form, hidden: true, available: false }, { headers: authHeaders() });
        await api.post(`/admin/products/${data.id}/draft`, form, { headers: authHeaders() });
      } else {
        await api.post(`/admin/products/${initial.id}/draft`, form, { headers: authHeaders() });
      }
      setDirty(false); onSaved("Brouillon enregistré");
    } catch (e) { setError(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  const doPublish = async () => {
    setConfirmPublish(false);
    if (!validate()) return;
    setBusy(true); setError("");
    try {
      if (isNew) await api.post("/admin/products", form, { headers: authHeaders() });
      else await api.put(`/admin/products/${initial.id}`, form, { headers: authHeaders() });
      setDirty(false); onSaved("Produit publié");
    } catch (e) { setError(e.response?.data?.detail || "Erreur"); setBusy(false); }
  };

  const tryClose = () => { if (dirty) setConfirmClose(true); else onClose(); };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={tryClose} />
      <div className="relative z-10 flex max-h-[94dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl" data-testid="product-editor">
        <div className="flex items-center justify-between border-b border-caneat-cream px-5 py-4">
          <div>
            <h3 className="caneat-title text-xl text-caneat-ink">{isNew ? "Nouveau produit" : "Modifier le produit"}</h3>
            {dirty && <span className="text-xs font-bold text-amber-600">Modifications non enregistrées</span>}
          </div>
          <button onClick={tryClose} className="flex h-8 w-8 items-center justify-center rounded-full bg-caneat-pageCream"><X className="h-5 w-5" /></button>
        </div>

        <div className="no-scrollbar flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-caneat-pageCream ring-1 ring-caneat-cream">
              {form.image ? <img src={resolveImage(form.image)} alt="" className="h-full w-full object-contain p-1" /> : <span className="text-xs text-caneat-muted">Photo</span>}
            </div>
            <div className="flex-1">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="product-image-input" />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 rounded-full bg-caneat-orange px-4 py-2 text-sm font-bold text-white">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Changer la photo
              </button>
              <p className="mt-2 text-xs text-caneat-muted">Optimisée automatiquement pour le web</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Slug (URL)</label>
              <input value={form.slug} onChange={(e) => upd({ slug: e.target.value })} data-testid="product-slug"
                className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" />
            </div>
            <div className="flex items-end">
              <label className="flex cursor-pointer items-center gap-2" data-testid="product-available-toggle">
                <input type="checkbox" checked={form.available} onChange={(e) => upd({ available: e.target.checked })} className="h-5 w-5 accent-caneat-orange" />
                <span className="text-sm font-bold text-caneat-ink">Disponible</span>
              </label>
            </div>
          </div>

          <div className="flex gap-1 rounded-full bg-caneat-pageCream p-1">
            {LANGS.map(([code, label]) => (
              <button key={code} onClick={() => setTab(code)}
                className={`flex-1 rounded-full py-2 text-xs font-bold ${tab === code ? "bg-caneat-orange text-white" : "text-caneat-muted"}`}>{label}</button>
            ))}
          </div>

          <div className="space-y-3">
            <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Nom ({tab})</label>
              <input value={form.name[tab] || ""} onChange={(e) => setML("name", tab, e.target.value)} data-testid={`product-name-${tab}`}
                className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" /></div>
            <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Description ({tab})</label>
              <textarea rows={2} value={form.description[tab] || ""} onChange={(e) => setML("description", tab, e.target.value)}
                className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
            <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Ingrédients ({tab})</label>
              <textarea rows={3} value={form.ingredients[tab] || ""} onChange={(e) => setML("ingredients", tab, e.target.value)} data-testid={`product-ingredients-${tab}`}
                className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
            <div><label className="mb-1 block text-xs font-bold uppercase text-caneat-muted">Détail allergènes ({tab})</label>
              <input value={form.allergens_detail[tab] || ""} onChange={(e) => setML("allergens_detail", tab, e.target.value)}
                className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" /></div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase text-caneat-muted">Allergènes (sélection)</label>
            <div className="flex flex-wrap gap-2">
              {ALLERGEN_KEYS.map((k) => {
                const on = form.allergens.includes(k);
                return (
                  <button key={k} onClick={() => toggleAllergen(k)} data-testid={`allergen-toggle-${k}`}
                    className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-bold ${on ? "border-caneat-orange bg-caneat-orange text-white" : "border-caneat-cream text-caneat-muted"}`}>
                    {on && <Check className="h-3.5 w-3.5" />} {ALLERGENS[k].fr}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase text-caneat-muted">Composition — % ({tab})</label>
            <div className="space-y-2">
              {(form.composition || []).map((c, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input value={c.pct || ""} onChange={(e) => { const comp = [...form.composition]; comp[idx] = { ...comp[idx], pct: e.target.value }; upd({ composition: comp }); }}
                    placeholder="%" className="w-16 rounded-xl border-2 border-caneat-cream px-2 py-2 text-sm outline-none focus:border-caneat-orange" />
                  <input value={c.label?.[tab] || ""} onChange={(e) => { const comp = [...form.composition]; comp[idx] = { ...comp[idx], label: { ...(comp[idx].label || {}), [tab]: e.target.value } }; upd({ composition: comp }); }}
                    placeholder={`Composant (${tab})`} className="flex-1 rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" />
                  <button onClick={() => upd({ composition: form.composition.filter((_, i) => i !== idx) })} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500"><X className="h-4 w-4" /></button>
                </div>
              ))}
              <button onClick={() => upd({ composition: [...(form.composition || []), { pct: "", label: { fr: "", nl: "", en: "" } }] })}
                className="flex items-center gap-1 rounded-full bg-caneat-orange/10 px-3 py-1.5 text-xs font-bold text-caneat-orange"><Plus className="h-3.5 w-3.5" /> Ajouter un composant</button>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-xs font-bold uppercase text-caneat-muted">Valeurs nutritionnelles (laisser vide si inconnu)</label>
            <div className="grid grid-cols-2 gap-2">
              {NUTRITION_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-[11px] font-semibold text-caneat-muted">{f.fr}{f.unit ? ` (${f.unit})` : ""}</label>
                  <input value={form.nutrition[f.key] || ""} onChange={(e) => upd({ nutrition: { ...form.nutrition, [f.key]: e.target.value } })} data-testid={`nutrition-${f.key}`}
                    className="w-full rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" />
                </div>
              ))}
            </div>
          </div>

          {error && <p className="text-sm font-bold text-red-500" data-testid="editor-error">{error}</p>}
        </div>

        <div className="flex flex-wrap gap-2 border-t border-caneat-cream p-4">
          <button onClick={() => setPreview(true)} data-testid="preview-btn"
            className="flex items-center justify-center gap-2 rounded-full bg-caneat-pageCream px-4 py-3 text-sm font-bold text-caneat-ink"><Eye className="h-4 w-4" /> Aperçu</button>
          <button onClick={saveDraft} disabled={busy} data-testid="save-draft-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-caneat-ink px-4 py-3 text-sm font-bold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Brouillon</button>
          <button onClick={() => setConfirmPublish(true)} disabled={busy} data-testid="publish-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-full bg-caneat-orange px-4 py-3 text-sm font-bold text-white disabled:opacity-60">
            <Send className="h-4 w-4" /> Publier</button>
        </div>
      </div>

      {preview && <PhonePreview form={form} onClose={() => setPreview(false)} />}
      {confirmPublish && <Confirm title="Confirmer la publication" message="Confirmer la publication de ces informations (ingrédients & allergènes) ? Elles seront visibles par les clients."
        confirmLabel="Publier" onConfirm={doPublish} onCancel={() => setConfirmPublish(false)} />}
      {confirmClose && <Confirm title="Quitter sans enregistrer ?" message="Des modifications ne sont pas enregistrées. Elles seront perdues."
        confirmLabel="Quitter" danger onConfirm={() => { setConfirmClose(false); onClose(); }} onCancel={() => setConfirmClose(false)} />}
    </div>, document.body);
}

export default function ProductsManager() {
  const { authHeaders, canEdit } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toast, setToast] = useState("");
  const dragIdx = useRef(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/products", { headers: authHeaders() });
    setProducts(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2500); };

  const toggleAvail = async (p) => {
    setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, available: !x.available } : x));
    await api.patch(`/admin/products/${p.id}/availability`, { available: !p.available }, { headers: authHeaders() });
  };
  const toggleHidden = async (p) => {
    setProducts((prev) => prev.map((x) => x.id === p.id ? { ...x, hidden: !x.hidden } : x));
    await api.patch(`/admin/products/${p.id}/hidden`, { hidden: !p.hidden }, { headers: authHeaders() });
  };
  const duplicate = async (p) => { await api.post(`/admin/products/${p.id}/duplicate`, {}, { headers: authHeaders() }); flash("Produit dupliqué"); load(); };
  const doDelete = async () => { await api.delete(`/admin/products/${toDelete.id}`, { headers: authHeaders() }); setToDelete(null); flash("Produit supprimé"); load(); };

  const persistOrder = async (list) => { await api.put("/admin/products/reorder/all", list.map((p) => p.id), { headers: authHeaders() }); };
  const move = async (index, dir) => {
    const next = [...products]; const t = index + dir;
    if (t < 0 || t >= next.length) return;
    [next[index], next[t]] = [next[t], next[index]];
    setProducts(next); persistOrder(next);
  };
  const onDrop = (index) => {
    const from = dragIdx.current; if (from === null || from === index) return;
    const next = [...products]; const [m] = next.splice(from, 1); next.splice(index, 0, m);
    dragIdx.current = null; setProducts(next); persistOrder(next);
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="caneat-title text-3xl text-caneat-ink">Produits CAN'EAT</h1>
          <p className="text-sm font-medium text-caneat-muted">{products.length} produit(s) · glisse pour réordonner</p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing(emptyProduct())} data-testid="add-product-btn"
            className="flex items-center gap-2 rounded-full bg-caneat-orange px-4 py-2.5 text-sm font-bold text-white shadow-lg"><Plus className="h-4 w-4" /> Ajouter</button>
        )}
      </div>

      {toast && <div className="mb-3 rounded-xl bg-emerald-100 px-4 py-2 text-sm font-bold text-emerald-700" data-testid="product-toast">{toast}</div>}

      <div className="space-y-3">
        {products.map((p, i) => (
          <div key={p.id} data-testid={`admin-product-${p.slug}`}
            draggable={canEdit}
            onDragStart={() => { dragIdx.current = i; }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className="flex flex-col gap-3 rounded-2xl bg-white p-3 ring-1 ring-caneat-cream sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-center gap-3">
            {canEdit && <GripVertical className="hidden h-5 w-5 shrink-0 cursor-grab text-caneat-muted sm:block" />}
            <img src={resolveImage(p.image)} alt="" className="h-16 w-16 shrink-0 rounded-xl bg-caneat-pageCream object-contain p-1" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="caneat-title truncate text-base text-caneat-ink">{p.name?.fr}</h3>
                {p.has_draft && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Brouillon</span>}
                {p.hidden && <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-[10px] font-bold text-zinc-600">Masqué</span>}
              </div>
              <p className="truncate text-xs text-caneat-muted">
                Modifié {p.updated_at ? new Date(p.updated_at).toLocaleDateString("fr-FR") : "—"}
              </p>
              {canEdit && (
                <label className="mt-1 inline-flex cursor-pointer items-center gap-2" data-testid={`avail-switch-${p.slug}`}>
                  <span className={`relative h-5 w-9 rounded-full transition-colors ${p.available ? "bg-emerald-500" : "bg-zinc-300"}`}>
                    <input type="checkbox" className="peer sr-only" checked={p.available} onChange={() => toggleAvail(p)} />
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${p.available ? "left-[18px]" : "left-0.5"}`} />
                  </span>
                  <span className="text-xs font-bold text-caneat-ink">{p.available ? "Disponible" : "Indisponible"}</span>
                </label>
              )}
            </div>
            </div>
            {canEdit && (
              <div className="flex flex-wrap items-center justify-end gap-1.5 border-t border-caneat-cream pt-2 sm:border-0 sm:pt-0">
                <button onClick={() => move(i, -1)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-pageCream text-caneat-muted sm:hidden"><ArrowUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-pageCream text-caneat-muted sm:hidden"><ArrowDown className="h-4 w-4" /></button>
                <button onClick={() => toggleHidden(p)} title="Masquer" data-testid={`hide-${p.slug}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-pageCream text-caneat-muted">
                  {p.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => duplicate(p)} title="Dupliquer" data-testid={`dup-${p.slug}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><Copy className="h-4 w-4" /></button>
                <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.slug}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-orange/10 text-caneat-orange"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(p)} data-testid={`delete-product-${p.slug}`} className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && <Editor initial={editing} onClose={() => setEditing(null)} onSaved={(m) => { setEditing(null); flash(m); load(); }} />}
      {toDelete && <Confirm title="Supprimer définitivement ?" message={`Le produit "${toDelete.name?.fr}" sera supprimé. Cette action est irréversible.`}
        confirmLabel="Supprimer" danger onConfirm={doDelete} onCancel={() => setToDelete(null)} />}
    </div>
  );
}
